import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../components/Toast';
import { adminApi } from '../services/api';
import { AdminSession, AdminUser, Role } from '../types';
import './Pages.css';

const AVAILABLE_PERMISSIONS: { entity: string; actions: string[] }[] = [
  { entity: 'clients', actions: ['view', 'create', 'edit', 'delete'] },
  { entity: 'contrats', actions: ['view', 'create', 'edit', 'delete'] },
  { entity: 'licenses', actions: ['view', 'create', 'delete'] },
  { entity: 'desktop_versions', actions: ['view', 'create', 'edit', 'publish', 'delete'] },
  { entity: 'ddns', actions: ['view', 'create', 'edit', 'renew', 'delete'] },
  { entity: 'creances', actions: ['view', 'create', 'edit', 'delete'] },
  { entity: 'support', actions: ['view', 'update', 'assign'] },
  { entity: 'admin', actions: ['access'] },
];

const PERMISSION_COLUMNS = ['view', 'create', 'edit', 'publish', 'renew', 'delete', 'access'];

const Admin: React.FC = () => {
  const { showToast, showConfirm } = useToast();

  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'sessions' | 'sms'>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [userForm, setUserForm] = useState({ user_name: '', password: '', role_id: 0 });

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({
    role_name: '',
    description: '',
    permissions: [] as { entity: string; action: string }[],
  });

  const [sessionSearch, setSessionSearch] = useState('');
  const [sessionTokenType, setSessionTokenType] = useState('');
  const [sessionStatus, setSessionStatus] = useState<'active' | 'all' | 'closed'>('active');

  const [webhookStatus, setWebhookStatus] = useState<Record<string, any> | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getRoles(),
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const response = await adminApi.getSessions({
        search: sessionSearch.trim(),
        token_type: sessionTokenType,
        status: sessionStatus,
      });
      setSessions(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      showToast(
        err.response?.data?.error || 'Erreur lors du chargement des sessions',
        'error'
      );
    } finally {
      setSessionsLoading(false);
    }
  }, [sessionSearch, sessionStatus, sessionTokenType, showToast]);

  const loadWebhookStatus = useCallback(async () => {
    try {
      setWebhookLoading(true);
      const response = await adminApi.getSmsWebhookStatus();
      setWebhookStatus(response.data && typeof response.data === 'object' ? response.data : null);
    } catch (err: any) {
      showToast(
        err.response?.data?.error || 'Erreur lors du chargement du statut webhook',
        'error'
      );
    } finally {
      setWebhookLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (activeTab === 'sms') {
      void loadWebhookStatus();
    }
  }, [activeTab, loadWebhookStatus]);

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({ user_name: '', password: '', role_id: 0 });
    setShowUserModal(true);
  };

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
    setUserForm({
      user_name: user.user_name,
      password: '',
      role_id: user.role_id || 0,
    });
    setShowUserModal(true);
  };

  const handleResetPassword = (user: AdminUser) => {
    showConfirm(
      `Reinitialiser le mot de passe de "${user.user_name}" vers "1" ?`,
      async () => {
        try {
          const response = await adminApi.updateUser(user.id, {
            user_name: user.user_name,
            password: '1',
            role_id: user.role_id,
          });
          const revokedCount = Number(response.data?.revoked_session_count || 0);
          showToast(
            revokedCount > 0
              ? `Mot de passe reinitialise, ${revokedCount} session(s) fermee(s)`
              : 'Mot de passe reinitialise',
            'success'
          );
          await loadSessions();
        } catch (err: any) {
          showToast(err.response?.data?.error || 'Erreur', 'error');
        }
      },
      'Reinitialiser le mot de passe'
    );
  };

  const handleToggleActive = async (user: AdminUser) => {
    try {
      const response = await adminApi.toggleActive(user.id);
      const revokedCount = Number(response.data?.revoked_session_count || 0);
      showToast(
        revokedCount > 0
          ? `Utilisateur ${user.is_active ? 'desactive' : 'active'}, ${revokedCount} session(s) fermee(s)`
          : `Utilisateur ${user.is_active ? 'desactive' : 'active'}`,
        'success'
      );
      await Promise.all([loadData(), loadSessions()]);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const data: { user_name: string; password?: string; role_id?: number } = {
          user_name: userForm.user_name,
          role_id: userForm.role_id,
        };
        if (userForm.password) {
          data.password = userForm.password;
        }
        const response = await adminApi.updateUser(editingUser.id, data);
        const revokedCount = Number(response.data?.revoked_session_count || 0);
        showToast(
          revokedCount > 0
            ? `Utilisateur mis a jour, ${revokedCount} session(s) fermee(s)`
            : 'Utilisateur mis a jour',
          'success'
        );
      } else {
        if (!userForm.password) {
          showToast('Le mot de passe est requis', 'error');
          return;
        }
        await adminApi.createUser(userForm);
        showToast('Utilisateur cree', 'success');
      }

      setShowUserModal(false);
      await Promise.all([loadData(), loadSessions()]);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const handleAddRole = () => {
    setEditingRole(null);
    setRoleForm({ role_name: '', description: '', permissions: [] });
    setShowRoleModal(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleForm({
      role_name: role.role_name,
      description: role.description,
      permissions: role.permissions.map((permission) => ({
        entity: permission.entity,
        action: permission.action,
      })),
    });
    setShowRoleModal(true);
  };

  const handleDeleteRole = (role: Role) => {
    showConfirm(
      `Supprimer le role "${role.role_name}" ?`,
      async () => {
        try {
          await adminApi.deleteRole(role.id);
          showToast('Role supprime', 'success');
          await loadData();
        } catch (err: any) {
          showToast(err.response?.data?.error || 'Erreur', 'error');
        }
      },
      'Supprimer le role'
    );
  };

  const handleSubmitRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await adminApi.updateRole(editingRole.id, roleForm);
        showToast('Role mis a jour', 'success');
      } else {
        await adminApi.createRole(roleForm);
        showToast('Role cree', 'success');
      }
      setShowRoleModal(false);
      await loadData();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erreur', 'error');
    }
  };

  const hasRolePerm = (entity: string, action: string): boolean =>
    roleForm.permissions.some(
      (permission) => permission.entity === entity && permission.action === action
    );

  const toggleRolePerm = (entity: string, action: string) => {
    if (hasRolePerm(entity, action)) {
      setRoleForm((prev) => ({
        ...prev,
        permissions: prev.permissions.filter(
          (permission) =>
            !(permission.entity === entity && permission.action === action)
        ),
      }));
      return;
    }

    setRoleForm((prev) => ({
      ...prev,
      permissions: [...prev.permissions, { entity, action }],
    }));
  };

  const handleRevokeSession = (session: AdminSession) => {
    showConfirm(
      `Fermer la session de "${session.display_name}" ?`,
      async () => {
        try {
          await adminApi.revokeSession(session.id);
          showToast('Session fermee', 'success');
          await loadSessions();
        } catch (err: any) {
          showToast(err.response?.data?.error || 'Erreur', 'error');
        }
      },
      'Fermer la session'
    );
  };

  const renderUsersTab = () => (
    <>
      <div style={{ marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={handleAddUser}>
          + Nouvel utilisateur
        </button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Role</th>
              <th>Statut</th>
              <th>Derniere connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Aucun utilisateur
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.user_name}</td>
                  <td>
                    <span
                      className={`status-badge ${user.role_name === 'ADMIN' ? 'active' : ''}`}
                    >
                      {user.role_name || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Jamais'}</td>
                  <td>
                    <div className="action-buttons" style={{ gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => handleEditUser(user)}
                      >
                        Editer
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => handleResetPassword(user)}
                      >
                        MDP = 1
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.is_active ? 'Desactiver' : 'Activer'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderRolesTab = () => (
    <>
      <div style={{ marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={handleAddRole}>
          + Nouveau role
        </button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Description</th>
              <th>Permissions</th>
              <th>Systeme</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Aucun role
                </td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    <strong>{role.role_name}</strong>
                  </td>
                  <td>{role.description}</td>
                  <td>{role.permissions.length} permissions</td>
                  <td>
                    <span className={`status-badge ${role.is_system ? 'active' : ''}`}>
                      {role.is_system ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons" style={{ gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => handleEditRole(role)}
                      >
                        Editer
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => handleDeleteRole(role)}
                        disabled={role.is_system}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderSmsTab = () => {
    const statusRow = (label: string, value: React.ReactNode) => (
      <tr>
        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</td>
        <td>{value}</td>
      </tr>
    );
    const boolBadge = (ok: boolean, yes: string, no: string) => (
      <span className={`status-badge ${ok ? 'active' : 'inactive'}`}>
        {ok ? yes : no}
      </span>
    );

    return (
      <>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void loadWebhookStatus()}
            disabled={webhookLoading}
          >
            {webhookLoading ? 'Chargement...' : 'Actualiser'}
          </button>
        </div>

        {webhookLoading && !webhookStatus ? (
          <div className="loading">Chargement du statut...</div>
        ) : !webhookStatus ? (
          <div className="empty-state">
            <p>Statut indisponible.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <tbody>
                {statusRow('URL du webhook', <code>{webhookStatus.webhook_url}</code>)}
                {statusRow(
                  'Secret configure',
                  boolBadge(
                    Boolean(webhookStatus.secret_configured),
                    'Oui',
                    'Non — renseigner Webhook_Secret dans config.ini'
                  )
                )}
                {statusRow('Relais d envoi', <code>{webhookStatus.relay_base_url}</code>)}
                {statusRow(
                  'Evenements acceptes (depuis demarrage)',
                  String(webhookStatus.accepted_count ?? 0)
                )}
                {statusRow(
                  'Requetes rejetees (signature invalide)',
                  String(webhookStatus.rejected_count ?? 0)
                )}
                {statusRow(
                  'Dernier evenement recu',
                  webhookStatus.last_received_at
                    ? `${new Date(webhookStatus.last_received_at).toLocaleString()} — ${
                        webhookStatus.last_event || '-'
                      }${webhookStatus.last_update_applied ? ' (statut mis a jour)' : ''}`
                    : 'Aucun depuis le demarrage du service'
                )}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  const renderSessionsTab = () => (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 1fr) 180px 180px auto',
          gap: '12px',
          alignItems: 'end',
          marginBottom: '16px',
        }}
      >
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Recherche</label>
          <input
            type="text"
            placeholder="Nom, identifiant ou IP"
            value={sessionSearch}
            onChange={(e) => setSessionSearch(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Type</label>
          <select
            value={sessionTokenType}
            onChange={(e) => setSessionTokenType(e.target.value)}
          >
            <option value="">Toutes</option>
            <option value="staff">Staff</option>
            <option value="client_portal">Client</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Etat</label>
          <select
            value={sessionStatus}
            onChange={(e) =>
              setSessionStatus(e.target.value as 'active' | 'all' | 'closed')
            }
          >
            <option value="active">Actives</option>
            <option value="all">Toutes</option>
            <option value="closed">Fermees</option>
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void loadSessions()}
            disabled={sessionsLoading}
          >
            Actualiser
          </button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Etat</th>
              <th>Type</th>
              <th>Compte</th>
              <th>Identifiant</th>
              <th>Role</th>
              <th>Derniere activite</th>
              <th>Creee le</th>
              <th>IP</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessionsLoading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Chargement des sessions...
                </td>
              </tr>
            ) : sessions.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Aucune session
                </td>
              </tr>
            ) : (
              sessions.map((session) => (
                <tr key={session.id}>
                  <td>
                    <span className={`status-badge ${session.is_active ? 'active' : 'inactive'}`}>
                      {session.is_active ? 'Active' : 'Fermee'}
                    </span>
                  </td>
                  <td>{session.token_type === 'client_portal' ? 'Client' : 'Staff'}</td>
                  <td>
                    <strong>{session.display_name || '-'}</strong>
                    {session.is_current ? (
                      <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                        Session courante
                      </div>
                    ) : null}
                  </td>
                  <td>{session.login_identifier || '-'}</td>
                  <td>{session.role_name || '-'}</td>
                  <td>
                    {session.last_seen_at
                      ? new Date(session.last_seen_at).toLocaleString()
                      : '-'}
                  </td>
                  <td>
                    {session.created_at
                      ? new Date(session.created_at).toLocaleString()
                      : '-'}
                  </td>
                  <td>{session.ip_address || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px' }}
                        onClick={() => handleRevokeSession(session)}
                        disabled={!session.is_active}
                      >
                        Fermer
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Administration</h1>
      </div>

      <div className="tabs" style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('users')}
        >
          Utilisateurs
        </button>
        <button
          className={`btn ${activeTab === 'roles' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('roles')}
        >
          Roles
        </button>
        <button
          className={`btn ${activeTab === 'sessions' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('sessions')}
        >
          Sessions
        </button>
        <button
          className={`btn ${activeTab === 'sms' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('sms')}
        >
          Webhook SMS
        </button>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : activeTab === 'users' ? (
        renderUsersTab()
      ) : activeTab === 'roles' ? (
        renderRolesTab()
      ) : activeTab === 'sms' ? (
        renderSmsTab()
      ) : (
        renderSessionsTab()
      )}

      {showUserModal ? (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}</h2>
            <form onSubmit={handleSubmitUser}>
              <div className="form-group">
                <label>Nom d'utilisateur</label>
                <input
                  type="text"
                  value={userForm.user_name}
                  onChange={(e) =>
                    setUserForm((prev) => ({ ...prev, user_name: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  {editingUser
                    ? 'Nouveau mot de passe (laisser vide pour ne pas changer)'
                    : 'Mot de passe'}
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) =>
                    setUserForm((prev) => ({ ...prev, password: e.target.value }))
                  }
                  required={!editingUser}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={userForm.role_id}
                  onChange={(e) =>
                    setUserForm((prev) => ({
                      ...prev,
                      role_id: Number(e.target.value),
                    }))
                  }
                >
                  <option value={0}>Aucun role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUserModal(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Mettre a jour' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showRoleModal ? (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <h2>{editingRole ? 'Modifier le role' : 'Nouveau role'}</h2>
            <form onSubmit={handleSubmitRole}>
              <div className="form-group">
                <label>Nom du role</label>
                <input
                  type="text"
                  value={roleForm.role_name}
                  onChange={(e) =>
                    setRoleForm((prev) => ({ ...prev, role_name: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={roleForm.description}
                  onChange={(e) =>
                    setRoleForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <div
                  style={{
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary, #f5f5f5)' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Entite</th>
                        {PERMISSION_COLUMNS.map((action) => (
                          <th
                            key={action}
                            style={{ padding: '8px', textAlign: 'center', fontSize: '0.85em' }}
                          >
                            {action}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {AVAILABLE_PERMISSIONS.map((permission) => (
                        <tr
                          key={permission.entity}
                          style={{ borderTop: '1px solid var(--border-color, #eee)' }}
                        >
                          <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                            {permission.entity}
                          </td>
                          {PERMISSION_COLUMNS.map((action) => (
                            <td key={action} style={{ padding: '8px', textAlign: 'center' }}>
                              {permission.actions.includes(action) ? (
                                <input
                                  type="checkbox"
                                  checked={hasRolePerm(permission.entity, action)}
                                  onChange={() =>
                                    toggleRolePerm(permission.entity, action)
                                  }
                                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                />
                              ) : (
                                <span style={{ color: '#ccc' }}>-</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRoleModal(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRole ? 'Mettre a jour' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Admin;
